/**
 * Game modes + feature registry.
 *
 * - **Environment** (`DHIRAJ_ENV` / `REACT_APP_DHIRAJ_ENV`): development vs production build.
 * - **Mode**: play ruleset the player picks (Regular / Sandbox / Dev).
 *
 * **Dev mode** always enables every feature (local playground).
 * Other modes only get features whose `modes` list includes them.
 * Promote a feature to prod by adding `'sandbox'` and/or `'regular'` to its list.
 */

import { DHIRAJ_ENV, type AppEnv } from './environment';

export type GameModeId = 'regular' | 'sandbox' | 'dev';

export type FeatureId =
  /** Corner-bracket mode label on home + in-run HUD */
  | 'modeBadge'
  /** Infinite missile/rail ammo + ∞ HUD glyphs */
  | 'infiniteAmmo'
  /** Sandbox/dev tool stack: spawn enemy/ally, immortal, obstacles */
  | 'sandboxTools'
  /** Enemy spawn + count control */
  | 'enemySpawn'
  /** Teammate spawn + count control */
  | 'teammateSpawn'
  /** Immortal / god-mode toggle */
  | 'immortal'
  /** Stop/start obstacles + block cap slider */
  | 'obstacleControls'
  /** Terrain garage picker (home) */
  | 'terrainPicker'
  /** Spaceship garage picker (home) */
  | 'shipPicker';

/** Which modes may show a feature (Dev mode ignores this and gets everything). */
export const FEATURES: Record<FeatureId, { modes: readonly GameModeId[] }> = {
  modeBadge: { modes: ['dev', 'sandbox'] },
  infiniteAmmo: { modes: ['dev', 'sandbox'] },
  sandboxTools: { modes: ['dev', 'sandbox'] },
  enemySpawn: { modes: ['dev', 'sandbox'] },
  teammateSpawn: { modes: ['dev', 'sandbox'] },
  immortal: { modes: ['dev', 'sandbox'] },
  obstacleControls: { modes: ['dev', 'sandbox'] },
  // Garage: Dev only for now — promote by adding 'sandbox' later
  terrainPicker: { modes: ['dev'] },
  shipPicker: { modes: ['dev'] },
};

/** Modes offered in Start → Modes for this app environment. */
export function availableModes(env: AppEnv = DHIRAJ_ENV): readonly GameModeId[] {
  if (env === 'development') {
    return ['dev', 'sandbox', 'regular'];
  }
  return ['sandbox', 'regular'];
}

/** Default mode when the app boots. */
export function defaultGameMode(env: AppEnv = DHIRAJ_ENV): GameModeId {
  return env === 'development' ? 'dev' : 'regular';
}

/** Clamp a mode if it is not allowed in this environment (e.g. 'dev' on prod). */
export function resolveGameMode(mode: GameModeId, env: AppEnv = DHIRAJ_ENV): GameModeId {
  const allowed = availableModes(env);
  if (allowed.includes(mode)) {
    return mode;
  }
  return defaultGameMode(env);
}

/**
 * Whether a feature is active for the given play mode.
 * Dev mode always returns true (full toolbox).
 */
export function isFeatureEnabled(feature: FeatureId, mode: GameModeId): boolean {
  if (mode === 'dev') {
    return true;
  }
  return FEATURES[feature].modes.includes(mode);
}

/** Sandbox-like rules: infinite ammo, tool HUD, no scored “serious” loadout. */
export function isSandboxLikeMode(mode: GameModeId): boolean {
  return isFeatureEnabled('infiniteAmmo', mode);
}

/** Home / HUD corner label for the mode (null = no badge). */
export function modeBadgeLabel(mode: GameModeId): 'DEV' | 'SANDBOX' | null {
  if (!isFeatureEnabled('modeBadge', mode)) {
    return null;
  }
  if (mode === 'dev') {
    return 'DEV';
  }
  if (mode === 'sandbox') {
    return 'SANDBOX';
  }
  return null;
}

/** Display name in the Modes submenu. */
export function modeMenuLabel(mode: GameModeId): string {
  switch (mode) {
    case 'dev':
      return 'Dev';
    case 'sandbox':
      return 'Sandbox';
    case 'regular':
      return 'Regular';
    default:
      return mode;
  }
}
